// @ts-strict-ignore
import { AbstractControl, FormControl, ValidationErrors, ValidatorFn, Validators } from '@angular/forms';
import ip from './util/ip-regex';
import { NumberUtils } from './util/number-utils';
import { StringUtils } from './util/string-utils';
import { ArrayUtils } from './util/array-utils';
import { LocaleResourceManager } from './manager/locale-resource.manager';
import { isArray } from 'lodash';

export namespace CustomValidators {
  export function trimRequired(c: FormControl): ValidationErrors | null {

    const value = c.value || ``;
    if (!value.trim()) {
      return {
        messageKey: `validation.error.required_field`
      };
    }

  }

  export function numRequired(c: AbstractControl): ValidationErrors | null {
    const value: number = !!c.value || c.value === 0 ? c.value : undefined;
    if (value === undefined || Number.isNaN(Number(value))) {
      return {
        messageKey: `validation.error.required_field`
      };
    }
    return null;
  }

  export function intRequired(c: AbstractControl): ValidationErrors | null {
    const value: number = !!c.value || c.value === 0 ? c.value : undefined;
    if (value === undefined || !Number.isInteger(Number(value))) {
      return {
        messageKey: `validation.error.integer_only`
      }
    }
    return null;
  }

  export function required(c: FormControl): ValidationErrors | null {
    const value = c.value || null;
    if (!value || (isArray(value) && value.length < 1)) {
      return {
        messageKey: `validation.error.required_field`
      };
    }
  }

  export function requiredCheckboxForRequest(otherControlName: string, internalMailPostfixPattern: string) {
    return (c: FormControl): ValidationErrors | null => {
      return checkboxForRequestRequired(c, otherControlName, internalMailPostfixPattern);
    };
  }

  function checkboxForRequestRequired(c: FormControl, otherControlName: string, internalMailMatchPattern: string): ValidationErrors | null {

    let otherControl: FormControl;

    if (!c.parent) {
      return null;
    }

    otherControl = c.parent.get(otherControlName) as FormControl;

    if (!otherControl) {
      throw new Error('checkboxForRequestRequired(): other control is not found in parent group');
    }

    const otherControlValue: string = otherControl.value.trim();
    if (!otherControl.invalid && otherControlValue) {
      if (!isInternalEmail(otherControlValue, internalMailMatchPattern)) {
        if (!c.value) {
          return {
            messageKey: `validation.error.confirmation_of_sending_to_external_email_missing`
          };
        }
      }
    }
  }

  function isInternalEmail(email: string, internalMailMatchPattern: string): boolean {
    return !!email.toLowerCase().match(internalMailMatchPattern);
  }

  export function emailOrEmptyWithOptions(c: FormControl, containsDomainPart: boolean): ValidationErrors | null {

    const disallowedLocalnameChars = '()<>,;:\\"[] `~!#$%^&*={}|/?\'';
    const disallowedDomainChars = '()<>,;:\\"[] `~!#$%^&*+={}|/?\'';

    const result = { messageKey: `` };
    const value = c.value || ``;

    if (!value.trim()) {
      return;
    }

    const atSignsCount = (value.match(/@/g) || []).length;
    if (atSignsCount === 0 && containsDomainPart) {
      result.messageKey = `validation.error.missing_at_sign`;
      return result;
    }
    if ((atSignsCount > 1 && containsDomainPart) || (atSignsCount > 0 && !containsDomainPart)) {
      result.messageKey = `validation.error.too_many_at_signs`;
      return result;
    }

    let username = '';
    let domain = '';
    if (containsDomainPart) {
      const emailParts = value.split(`@`);
      username = emailParts[0];
      domain = emailParts[1];
    } else {
      username = value;
    }

    if (!username) {
      result.messageKey = `validation.error.missing_username_error`;
      return result;
    }

    if (username.split(``).filter(usernameChar => disallowedLocalnameChars.indexOf(usernameChar) > -1).length > 0) {
      result.messageKey = `validation.error.contains_invalid_character`;
      return result;
    }

    if (!containsDomainPart) {
      return;
    }

    if (domain.length && domain[0] === `[` && domain[domain.length - 1] === `]`) {

      // if domain is ip

      if (!ip({ exact: true }).test(domain.substr(1, domain.length - 2))) {
        result.messageKey = `validation.error.invalid_ip_domain`;
        return result;
      }
    } else {

      const dotsCount = (domain.match(/\./g) || []).length;
      if (dotsCount === 0) {
        result.messageKey = `validation.error.missing_period_in_domain`;
        return result;
      } else if (dotsCount === 1) {
        const domainLastPart = domain.split(`.`)[1];
        // SMAR-537: Only allowing specific domain lengths was ported from flex version where email validator provided by flex framework did include such checking by default.
        // So such checking was not expressively/specifically introduced for the smartDesigner. In scope of SMAR-537 we allow now any positive length (> 0).
        /*if ([2, 3, 4, 6].indexOf(domainLastPart.length) === -1) {
          result.messageKey = `validation.error.invalid_domain`;
          return result;
        }*/
        if (domainLastPart.length === 0) {
          result.messageKey = `validation.error.invalid_domain`;
          return result;
        }
      } else if (domain.indexOf(`..`) > -1) {
        result.messageKey = `validation.error.invalid_periods_in_domain`;
        return result;
      }

      if (domain.split(``).filter(domainChar => disallowedDomainChars.indexOf(domainChar) > -1).length > 0) {
        result.messageKey = `validation.error.invalid_char`;
        return result;
      }

      if (domain[0] === `.`) {
        result.messageKey = `validation.error.invalid_domain`;
        return result;
      }
    }
  }

  export function email(c: FormControl): ValidationErrors | null {
    return emailWithOptions(c, true);
  }

  export function emailWithOptions(c: FormControl, containsDomain: boolean): ValidationErrors | null {

    const result = { messageKey: `` };
    const value = c.value;

    if (!value.trim()) {
      result.messageKey = `validation.error.required_field`;
      return result;
    }

    return emailOrEmptyWithOptions(c, containsDomain);
  }

  export function emailValidator(containsDomain: boolean) {
    return (c: FormControl): ValidationErrors | null => {
      return emailOrEmptyWithOptions(c, containsDomain);
    };
  }


  export function number(formatNumber: (value:number) => string, thousandsSeparator: string, decimalSeparator: string, min: number = null, max: number = null,
      minError: string = null, maxError: string = null, precision: number | false = 1, allowNegative: boolean = true): ValidatorFn {
    const decimalPointCountError: string = 'validation.error.decimal_point_count';
    const exceedsMaxError: string = 'validation.error.exceeds_max';
    const lowerThanMinError: string = 'validation.error.lower_than_min';
    const precisionError: string = 'validation.error.precision';
    const separationError: string = 'validation.error.separation';
    const invalidCharError: string = 'validation.error.contains_invalid_character';
    const negativeError: string = 'validation.error.negative';

    return (c: FormControl): ValidationErrors | null => {
      // Check if the input is negative.
      if (c.value.length > 0 && c.value.charAt(0) === '-') {
        if (c.value.length === 1) { // we have only '-' char
          return {
            messageKey: invalidCharError
          };
        } else if (c.value.length === 2 && c.value.charAt(1) === '.') { // handle "-."
          return {
            messageKey: invalidCharError
          };
        }

        // Check if negative input is allowed.
        if (!allowNegative) {
          return {
            messageKey: negativeError
          };
        }
      }

      const separatedByDecimal: string[] = String(c.value).trim().split(decimalSeparator);

      if (separatedByDecimal.length > 2) {
        return {
          messageKey: decimalPointCountError
        };
      }

      if (separatedByDecimal.length > 1) {
         // we only have a '.'
        if (String(c.value).trim().length === 1) {
          return {
            messageKey: invalidCharError
          };
        }

        if (NumberUtils.isNumber(precision) && separatedByDecimal[1].length > precision) {
          return {
            messageKey: precisionError
          };
        }

        const DECIMAL_DIGITS: string = '0123456789';
        for (let i = 0; i < separatedByDecimal[1].length; i++) {
          // This character must be a digit.
          if (DECIMAL_DIGITS.indexOf(separatedByDecimal[1].charAt(i)) === -1) {
            return {
              messageKey: invalidCharError
            };
          }
        }
      }

      const separatetByThousand: string[] = separatedByDecimal[0].replace('-', '').split(thousandsSeparator);
      if (separatetByThousand.length > 1) {
        if (separatetByThousand[0].length > 3) {
          return {
            messageKey: separationError
          };
        }
        for (let i = 1; i < separatetByThousand.length; i++) {
          if (separatetByThousand[i].length !== 3) {
            return {
              messageKey: separationError
            };
          }
        }
      }

      const number: number = +String(c.value).trim().replace(thousandsSeparator, '').replace(decimalSeparator, '.');
      if (NumberUtils.isNumber(max) && number > max) {
        return {
          messageKey: maxError ? maxError : exceedsMaxError,
          param: formatNumber(max)
        };
      }
      if (NumberUtils.isNumber(min) && number < min) {
        return {
          messageKey: minError ? minError : lowerThanMinError,
          param: formatNumber(min)
        };
      }
    };
  }

  export function integer(c: FormControl): ValidationErrors | null {
    const value: number = +c.value;
    return Number.isInteger(value) ? null : {messageKey: 'validation.error.no_integer'};
  }

  export function inAllowedNumbers(allowedNumbers: number[], message: string): ValidatorFn {
    return (c: FormControl): ValidationErrors | null => {
      if (!ArrayUtils.isEmpty(allowedNumbers)) {
        const number: number = Number(c.value);
        for (const allowedNumber of allowedNumbers) {
          if (NumberUtils.equals(number, allowedNumber)) {
            return;
          }
        }
        return {
          messageKey: message
        };
      }
    };
  }

  export function maxLength(maxLength: number): ValidatorFn {
    return (c: FormControl): ValidationErrors | null => {
      if (c.value && c.value.length > maxLength) {
        return {
          messageKey: 'validation.error.too_many_chars',
          param: maxLength
        };
      }
    };
  }

  export function minLength(minLength: number): ValidatorFn {
    return (c: FormControl): ValidationErrors | null => {
      if (c.value && c.value.length < minLength) {
        return {
          messageKey: 'validation.error.too_few_chars',
          param: minLength
        };
      }
    };
  }

  export function minMaxLength(minLength: number, maxLength: number,
                               tooShortError: string, tooLongError: string): ValidatorFn {
    return (c: FormControl): ValidationErrors | null => {
      const valueToCheck: string = c.value || '';
      if (valueToCheck.length > maxLength) {
        return {
          messageKey: tooLongError,
          param: maxLength
        };
      }
      if (valueToCheck.length < minLength) {
        return {
          messageKey: tooShortError,
          param: minLength
        };
      }
    };
  }

  export function matchEmailsValidator(otherControlName: string) {

    let thisControl: FormControl;
    let otherControl: FormControl;

    return function matchOtherValidate(control: FormControl) {

      if (!control.parent) {
        return null;
      }

      // Initializing the validator.
      if (!thisControl) {
        thisControl = control;
        otherControl = control.parent.get(otherControlName) as FormControl;
        if (!otherControl) {
          throw new Error('matchOtherValidator(): other control is not found in parent group');
        }
        otherControl.valueChanges.subscribe(() => {
          thisControl.updateValueAndValidity();
        });
      }

      if (!otherControl) {
        return null;
      }

      if (otherControl.value !== thisControl.value) {
        return {
          messageKey: 'validation.error.emails_do_not_match',
        };
      }

      return null;
    };
  }

  export interface DefaultNameValidatorParameters {
    allowInternationalCharacters?: boolean;
    allowLineBreaks?: boolean;
    weakValidation?: boolean;
    maxLines?: number;
    maxCharsPerLine?: number;
    allowAdditionalCharacters?: string;
    minCharacters?: number;
    required?: boolean;
  }

  export function defaultNameValidator(localeResourceManager: LocaleResourceManager, parameters: DefaultNameValidatorParameters): ValidatorFn {
    const weakValidationExp: RegExp = /^.*$/;
    const weakValidationWithLineBreaksExp: RegExp = /^(.|\n|\r)*$/;
    const defaultAllowedCharacters: string = 'üöäÜÖÄ²³\\w.:;,\\! \\+\\-\\(\\)';

    const allowInternationalCharacters: boolean = parameters.hasOwnProperty('allowInternationalCharacters') ?
      parameters.allowInternationalCharacters : false;
    const allowLineBreaks: boolean = parameters.hasOwnProperty('allowLineBreaks') ? parameters.allowLineBreaks : false;
    const weakValidation: boolean = parameters.hasOwnProperty('weakValidation') ? parameters.weakValidation : false;
    const maxLines: number = parameters.hasOwnProperty('maxLines') ? parameters.maxLines : -1;
    const maxCharsPerLine: number = parameters.hasOwnProperty('maxCharsPerLine') ? parameters.maxCharsPerLine : -1;
    const allowAdditionalCharacters: string = parameters.hasOwnProperty('allowAdditionalCharacters') ? parameters.allowAdditionalCharacters : '';
    const minCharacters: number = parameters.hasOwnProperty('minCharacters') ? parameters.minCharacters : 0;
    const required: boolean = parameters.hasOwnProperty('required') ? parameters.required : true;

    const tooManyLinesError: string = 'validation.error.too_many_lines';
    const tooManyCharsPerLineError: string = 'validation.error.too_many_chars';
    const tooFewCharactersError: string = 'validation.error.too_few_chars';
    const requiredFieldError: string = 'validation.error.required_field';

    const noMatchError = localeResourceManager.getValueByKey('validation.error.contains_invalid_character') +
      '\n' + localeResourceManager.getValueByKey('validation.error.allowedCharacters') + '[a..z][0..9]' +
      defaultAllowedCharacters.replace(/\\/g, '');

    return (c: FormControl): ValidationErrors | null => {
      const value: string = c.value || '';

      if (!value.trim()) {
        if (required) {
          return {
            messageKey: requiredFieldError
          };
        } else {
          return null;
        }
      }

      if (minCharacters > 0 && value.length < minCharacters && (required || value.length > 0)) {
        return {
          messageKey: tooFewCharactersError,
          param: minCharacters
        };
      } else if (maxLines >= 0 && StringUtils.getLineCount(value) > maxLines) {
        return {
          messageKey: tooManyLinesError,
          param: maxLines
        };
      } else if (maxCharsPerLine >= 0 && StringUtils.getMaxCharsPerLine(value) > maxCharsPerLine) {
        return {
          messageKey: tooManyCharsPerLineError,
          param: maxCharsPerLine
        };
      }

      let expression: string = '';
      if (weakValidation) {
        if (allowLineBreaks) {
          expression = weakValidationWithLineBreaksExp.source;
        } else {
          expression = weakValidationExp.source;
        }
      } else if (allowInternationalCharacters) {
        if (allowLineBreaks) {
          expression = weakValidationWithLineBreaksExp.source;
        } else {
          expression = weakValidationExp.source;
        }
      } else {
        let exp: string = '^[' + defaultAllowedCharacters + allowAdditionalCharacters;
        if (allowLineBreaks) {
          exp += '\n\r';
        }
        exp += ']+$';
        expression = exp;
      }

      const results: any[] = [];
      const regExp: RegExp = new RegExp(expression);

      if (regExp && StringUtils.isNotEmpty(expression)) {
        let result: RegExpExecArray | null = regExp.exec(String(value));
        if (regExp.global) {
          while (result) {
            results.push(result[0]);
            result.slice(0, 1);
            result = regExp.exec(String(value));
          }
        } else if (ArrayUtils.isNotEmpty(result)) {
          results.push(result[0]);
          result.slice(0, 1);
        }

        if (ArrayUtils.isEmpty(results)) {
          return {
            messageKey: noMatchError
          };
        }
      }

      return null;
    };
  }

  export function projectNameValidator(excludeAllSpecialCharacters: boolean = false): ValidatorFn {
    return (formControl: FormControl): ValidationErrors | null => {
      if (!checkProjectNameIsValid(formControl.value, excludeAllSpecialCharacters)) {
        return {
          messageKey: `validation.error.contains_invalid_character`,
        };
      }
    };
  }

  export function checkProjectNameIsValid(value: string, excludeAllSpecialCharacters: boolean = false): boolean {
    if (StringUtils.TAB_AND_NEW_LINE_REGEXP.exec(value)) {
      return false;
    }
    const specialCharactersRegex = excludeAllSpecialCharacters
      ? StringUtils.DISALLOWED_PROJECT_NAME_FULL_CHARS_REGEXP
      : StringUtils.DISALLOWED_PROJECT_NAME_CHARS_REGEXP;
    if (value.match(specialCharactersRegex)) {
      return false;
    }
    return true;
  }

  export function min(min: number): ValidatorFn {
    return (c: FormControl): ValidationErrors | null => {
      const control = new FormControl(c.value, Validators.min(min));
      if (control.errors) {
        return {
          messageKey: 'validation.error.lower_than_min',
          param: min,
        };
      }
    };
  }

  export function max(max: number): ValidatorFn {
    return (c: FormControl): ValidationErrors | null => {

      const valueToCheck: number = c.value || 0;
      if (valueToCheck > max) {
        return {
          messageKey: 'validation.error.exceeds_max',
          param: max,
        };
      }
    };
  }

  export function validArticleNoLong(control: FormControl): ValidationErrors | null {
    const validationString = control.value + ('/');
    const regExp = new RegExp('^([0-9A-Z]{4}-[0-9A-Z]{4}\/)*$');
    return !!(validationString.match(regExp)) ? null : {messageKey: `finalizeProject.label.articleNumber.input.error`};
  }

  export function hexColor(): ValidatorFn {
    return (c: FormControl): ValidationErrors | null => {
      const inputString = c.value as string;

      const matches = /^#(?:[0-9a-fA-F]{6})$/.test(inputString);

      if(!matches) {
        return {
          messageKey: 'validation.error.hexcode',
        };
      }
    }
  }
}
